import React, { useState } from "react";
import { AlertCircle, Check, ChevronDown, Loader2, RotateCcw, UploadCloud, X } from "lucide-react";
import { Button, Modal, cx } from "../../../pages/admin/_shared";
import { fmtBytes, iconOfExt, extOf } from "./mediaUtils";

/** Xác nhận trước khi tải lên: số lượng, tổng dung lượng, thư mục đích. */
export function UploadStagingModal({ files, prefix, onCancel, onConfirm, onRemove }) {
  if (!files?.length) return null;
  const total = files.reduce((s, f) => s + f.size, 0);
  return (
    <Modal
      open
      onClose={onCancel}
      title={
        <span className="flex items-center gap-2 text-base font-semibold text-admin-text">
          <UploadCloud size={18} className="text-admin-primary" /> Tải lên {files.length} tệp
        </span>
      }
      maxWidthClass="max-w-xl"
      footer={
        <div className="flex items-center justify-between gap-2">
          <p className="text-xs text-admin-textMuted">
            Tổng <b className="text-admin-text">{fmtBytes(total)}</b> → <span className="font-mono">/{prefix || ""}</span>
          </p>
          <div className="flex gap-2">
            <Button onClick={onCancel}>Huỷ</Button>
            <Button variant="primary" icon={UploadCloud} onClick={onConfirm}>
              Tải lên
            </Button>
          </div>
        </div>
      }
    >
      <ul className="max-h-[320px] divide-y divide-admin-border overflow-y-auto rounded-xl border border-admin-border">
        {files.map((f, i) => {
          const Icon = iconOfExt(extOf(f.name));
          return (
            <li key={`${f.name}-${i}`} className="flex items-center gap-3 px-3 py-2">
              <Icon size={16} className="shrink-0 text-admin-textMuted" />
              <span className="min-w-0 flex-1 truncate text-sm text-admin-text" title={f.name}>
                {f.name}
              </span>
              <span className="shrink-0 text-xs text-admin-textMuted">{fmtBytes(f.size)}</span>
              <button type="button" onClick={() => onRemove(i)} className="shrink-0 rounded p-1 text-admin-textMuted hover:bg-admin-muted hover:text-admin-danger" aria-label="Bỏ tệp này">
                <X size={14} />
              </button>
            </li>
          );
        })}
      </ul>
    </Modal>
  );
}

/** Hàng đợi tải lên cố định góc dưới phải. */
export function UploadQueuePanel({ jobs, onRetry, onCancel, onClear }) {
  const [open, setOpen] = useState(true);
  if (!jobs.length) return null;
  const done = jobs.filter((j) => j.status === "done").length;
  const failed = jobs.filter((j) => j.status === "error").length;
  const active = jobs.filter((j) => j.status === "queued" || j.status === "uploading").length;
  const total = jobs.reduce((s, j) => s + j.size, 0) || 1;
  const loaded = jobs.reduce((s, j) => s + (j.status === "done" ? j.size : j.loaded || 0), 0);
  const pct = Math.min(100, Math.round((loaded / total) * 100));

  return (
    <div className="fixed bottom-4 right-4 z-[120] w-[min(380px,calc(100vw-2rem))] overflow-hidden rounded-2xl border border-admin-border bg-admin-surface shadow-2xl">
      <button type="button" onClick={() => setOpen((o) => !o)} className="flex w-full items-center gap-3 px-4 py-3 text-left">
        <span className={cx("flex h-8 w-8 items-center justify-center rounded-full", active ? "bg-admin-primaryLight text-admin-primary" : failed ? "bg-rose-50 text-rose-600" : "bg-emerald-50 text-emerald-600")}>
          {active ? <Loader2 size={16} className="animate-spin" /> : failed ? <AlertCircle size={16} /> : <Check size={16} />}
        </span>
        <span className="min-w-0 flex-1">
          <span className="block text-sm font-semibold text-admin-text">
            {active ? `Đang tải lên ${active} tệp…` : failed ? `${failed} tệp lỗi` : `Đã tải lên ${done} tệp`}
          </span>
          <span className="block text-xs text-admin-textMuted">
            {done}/{jobs.length} xong · {fmtBytes(loaded)} / {fmtBytes(total)}
          </span>
        </span>
        <ChevronDown size={16} className={cx("text-admin-textMuted transition", open && "rotate-180")} />
      </button>
      <div className="h-1 bg-admin-muted">
        <div className={cx("h-full transition-all", failed && !active ? "bg-rose-500" : "bg-admin-primary")} style={{ width: `${pct}%` }} />
      </div>
      {open ? (
        <>
          <ul className="max-h-64 divide-y divide-admin-border overflow-y-auto">
            {jobs.map((j) => {
              const p = j.size ? Math.min(100, Math.round(((j.status === "done" ? j.size : j.loaded || 0) / j.size) * 100)) : 0;
              return (
                <li key={j.id} className="px-4 py-2.5">
                  <div className="flex items-center gap-2">
                    <span className="min-w-0 flex-1 truncate text-[13px] text-admin-text" title={j.name}>
                      {j.name}
                    </span>
                    {j.status === "done" ? <Check size={14} className="text-emerald-600" /> : null}
                    {j.status === "error" || j.status === "canceled" ? (
                      <button type="button" onClick={() => onRetry(j.id)} className="inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[11px] font-semibold text-admin-primary hover:bg-admin-primaryLight">
                        <RotateCcw size={11} /> Thử lại
                      </button>
                    ) : null}
                    {j.status === "uploading" || j.status === "queued" ? (
                      <button type="button" onClick={() => onCancel(j.id)} className="rounded p-0.5 text-admin-textMuted hover:text-admin-danger" aria-label="Huỷ">
                        <X size={14} />
                      </button>
                    ) : null}
                  </div>
                  {j.status === "uploading" || j.status === "queued" ? (
                    <div className="mt-1.5 flex items-center gap-2">
                      <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-admin-muted">
                        <div className="h-full rounded-full bg-admin-primary transition-all" style={{ width: `${p}%` }} />
                      </div>
                      <span className="w-14 text-right text-[11px] text-admin-textMuted">{j.status === "queued" ? "Chờ" : `${p}%`}</span>
                    </div>
                  ) : (
                    <p className={cx("mt-0.5 text-[11px]", j.status === "error" ? "text-rose-600" : "text-admin-textMuted")}>
                      {j.status === "error" || j.status === "canceled" ? j.error : fmtBytes(j.size)}
                    </p>
                  )}
                </li>
              );
            })}
          </ul>
          {!active ? (
            <div className="flex justify-end border-t border-admin-border px-3 py-2">
              <Button size="sm" variant="ghost" onClick={onClear}>
                Xoá danh sách
              </Button>
            </div>
          ) : null}
        </>
      ) : null}
    </div>
  );
}
